import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { ThemeModeSwitcher } from '@/theme/ThemeModeSwitcher';

type NavItem = {
  to: string;
  labelKey: string;
  icon: ReactNode;
  match: (path: string) => boolean;
  adminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    labelKey: 'nav.dashboard',
    icon: <SpaceDashboardRoundedIcon fontSize="small" />,
    match: (p) => p === '/' || p.startsWith('/dashboard'),
  },
  {
    to: '/projects',
    labelKey: 'nav.projects',
    icon: <TroubleshootRoundedIcon fontSize="small" />,
    match: (p) => p.startsWith('/projects'),
  },
  {
    to: '/patients',
    labelKey: 'nav.patients',
    icon: <GroupsRoundedIcon fontSize="small" />,
    match: (p) => p.startsWith('/patients'),
  },
  {
    to: '/reports',
    labelKey: 'nav.reports',
    icon: <AssessmentRoundedIcon fontSize="small" />,
    match: (p) => p.startsWith('/reports'),
  },
  {
    to: '/admin',
    labelKey: 'nav.admin',
    icon: <AdminPanelSettingsRoundedIcon fontSize="small" />,
    match: (p) => p.startsWith('/admin'),
    adminOnly: true,
  },
];

function TmsBrandLogo({ to = '/' }: { to?: string }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box
      component={Link}
      to={to}
      aria-label={t('appLayout.logoAria')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        textDecoration: 'none',
        color: 'inherit',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: '-0.04em',
          fontFamily: theme.typography.fontFamily,
        }}
        aria-hidden
      >
        T
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}>
        <Typography
          component="span"
          sx={{
            fontSize: '1rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'text.primary',
          }}
        >
          TMS
        </Typography>
      </Box>
    </Box>
  );
}

function HorizontalNav({
  items,
  pathname,
}: {
  items: NavItem[];
  pathname: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Box
      component="nav"
      aria-label={t('appLayout.menuAria')}
      sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'stretch', gap: 0, ml: 3, height: '100%' }}
    >
      {items.map((item) => {
        const active = item.match(pathname);
        return (
          <Box
            key={item.to}
            component={NavLink}
            to={item.to}
            sx={() => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.625,
              px: 1.75,
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
              color: active ? 'text.primary' : 'text.secondary',
              textDecoration: 'none',
              position: 'relative',
              transition: 'color 140ms ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 14,
                right: 14,
                bottom: 0,
                height: 2,
                borderRadius: '2px 2px 0 0',
                backgroundColor: active ? 'primary.main' : 'transparent',
                transition: 'background-color 140ms ease',
              },
              '&:hover': {
                color: 'text.primary',
                '&::after': { backgroundColor: active ? 'primary.main' : 'divider' },
              },
            })}
          >
            {item.icon}
            {t(item.labelKey)}
          </Box>
        );
      })}
    </Box>
  );
}

function CommandSearchPlaceholder(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('appLayout.searchTooltip')}>
      <Box
        component="button"
        type="button"
        aria-label={t('appLayout.searchAria')}
        sx={(theme) => ({
          display: { xs: 'none', md: 'inline-flex' },
          alignItems: 'center',
          gap: 1,
          height: 38,
          px: 1.75,
          minWidth: 280,
          maxWidth: 380,
          background: theme.palette.mode === 'light' ? '#F2F4F7' : alpha(theme.palette.background.default, 0.6),
          border: `1px solid transparent`,
          borderRadius: 999,
          color: theme.palette.text.secondary,
          fontSize: 13,
          fontFamily: theme.typography.fontFamily,
          cursor: 'pointer',
          transition: 'border-color 160ms ease, background-color 160ms ease',
          '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.mode === 'light' ? '#EAECEF' : theme.palette.background.default,
          },
        })}
      >
        <SearchRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        <Box sx={{ flex: 1, textAlign: 'left' }}>{t('appLayout.searchPlaceholder')}</Box>
        <Box
          component="kbd"
          sx={(theme) => ({
            fontFamily: 'monospace',
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 0.75,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          ⌘K
        </Box>
      </Box>
    </Tooltip>
  );
}

function MobileNavDrawer({
  open,
  onClose,
  items,
  pathname,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  pathname: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Drawer
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: 280 } }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <TmsBrandLogo />
      </Box>
      <Divider />
      <List sx={{ py: 1 }}>
        {items.map((item) => {
          const active = item.match(pathname);
          return (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              onClick={onClose}
              selected={active}
              sx={{ mx: 1, mb: 0.25 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: active ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
}

function UtilityBar(): JSX.Element {
  const theme = useTheme();
  const env = (import.meta.env.MODE ?? 'dev').toUpperCase();
  const today = new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar + 1,
        height: 32,
        alignItems: 'center',
        gap: 2,
        px: 3,
        fontSize: '0.75rem',
        color: 'text.secondary',
        backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'success.main',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.18)}`,
          }}
        />
        <Box component="span" sx={{ fontWeight: 500 }}>System OK</Box>
      </Stack>
      <Box
        component="span"
        sx={{
          px: 0.875,
          py: 0.125,
          borderRadius: 0.75,
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          bgcolor: env === 'PRODUCTION' ? alpha(theme.palette.error.main, 0.12) : alpha(theme.palette.warning.main, 0.18),
          color: env === 'PRODUCTION' ? theme.palette.error.main : theme.palette.warning.main,
          border: `1px solid ${env === 'PRODUCTION' ? alpha(theme.palette.error.main, 0.25) : alpha(theme.palette.warning.main, 0.3)}`,
        }}
      >
        {env}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ fontSize: '0.75rem' }}>
        <Box component={Link} to="/dashboard" sx={{ color: 'text.secondary', textDecoration: 'none', ':hover': { color: 'primary.main' } }}>
          Dashboard
        </Box>
        <Box component={Link} to="/reports" sx={{ color: 'text.secondary', textDecoration: 'none', ':hover': { color: 'primary.main' } }}>
          Raporty
        </Box>
        <Box component="a" href="/swagger-ui/index.html" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary', textDecoration: 'none', ':hover': { color: 'primary.main' } }}>
          API
        </Box>
        <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
        <Box component="span" sx={{ fontFamily: theme.typography.fontFamily, color: 'text.secondary' }}>{today}</Box>
      </Stack>
    </Box>
  );
}

export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [toolsMenu, setToolsMenu] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNav = NAV_ITEMS.filter((it) => !it.adminOnly || user?.role === 'ADMIN');

  const handleLogout = async (): Promise<void> => {
    setUserMenu(null);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <UtilityBar />
      <AppBar position="sticky" sx={{ top: { xs: 0, md: 32 } }}>
        <Toolbar
          sx={{
            gap: 1.5,
            minHeight: { xs: 60, md: 60 },
            px: { xs: 1.5, md: 3 },
          }}
        >
          {!isDesktop && (
            <IconButton
              edge="start"
              aria-label={t('appLayout.openMenuAria')}
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}

          <TmsBrandLogo />

          {user && <HorizontalNav items={visibleNav} pathname={location.pathname} />}

          <Box sx={{ flex: 1 }} />

          {user && <CommandSearchPlaceholder />}

          <Stack direction="row" spacing={0.5} alignItems="center">
            {user && (
              <Tooltip title={t('nav.tools')}>
                <IconButton
                  aria-label={t('nav.tools')}
                  onClick={(e) => setToolsMenu(e.currentTarget)}
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  <BuildRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Menu
              anchorEl={toolsMenu}
              open={Boolean(toolsMenu)}
              onClose={() => setToolsMenu(null)}
              PaperProps={{ sx: { mt: 0.5, minWidth: 220 } }}
            >
              <MenuItem
                component={Link}
                to="/dev/totp"
                onClick={() => setToolsMenu(null)}
              >
                <ListItemIcon>
                  <SecurityRoundedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                {t('nav.devTotp')}
              </MenuItem>
              <MenuItem
                component="a"
                href="/swagger-ui/index.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setToolsMenu(null)}
              >
                <ListItemIcon>
                  <AnalyticsRoundedIcon fontSize="small" color="info" />
                </ListItemIcon>
                {t('nav.swagger')}
              </MenuItem>
              <MenuItem
                component="a"
                href="/actuator/health"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setToolsMenu(null)}
              >
                <ListItemIcon>
                  <VerifiedUserRoundedIcon fontSize="small" color="success" />
                </ListItemIcon>
                {t('nav.health')}
              </MenuItem>
            </Menu>

            {user && <NotificationBell />}

            <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              <ThemeModeSwitcher />
            </Box>

            <Box sx={{ display: { xs: 'none', lg: 'inline-flex' } }}>
              <LanguageSwitcher />
            </Box>

            {user ? (
              <>
                <Box
                  sx={{
                    width: 1,
                    height: 24,
                    bgcolor: 'divider',
                    mx: 0.5,
                    display: { xs: 'none', sm: 'block' },
                  }}
                />
                <Button
                  onClick={(e) => setUserMenu(e.currentTarget)}
                  endIcon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    pl: 0.5,
                    pr: 1,
                    py: 0.5,
                    borderRadius: 999,
                    minHeight: 40,
                    ':hover': { bgcolor: 'action.hover' },
                  }}
                  aria-label={`${user.firstName} ${user.lastName} — menu konta`}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          border: `2px solid ${theme.palette.background.paper}`,
                        }}
                      />
                    }
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: '0.8125rem',
                        fontWeight: 700,
                        mr: 1,
                      }}
                    >
                      {user.firstName.charAt(0)}
                      {user.lastName.charAt(0)}
                    </Avatar>
                  </Badge>
                  <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                    <Typography
                      component="span"
                      sx={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.15 }}
                    >
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.6875rem',
                        color: 'text.secondary',
                        lineHeight: 1.15,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {user.role}
                    </Typography>
                  </Box>
                </Button>
                <Menu
                  anchorEl={userMenu}
                  open={Boolean(userMenu)}
                  onClose={() => setUserMenu(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { mt: 0.5, minWidth: 240 } }}
                >
                  <Box sx={{ px: 2, py: 1.25 }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Zalogowany jako
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mt: 0.25 }}>
                      {user.email}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                      <Chip
                        label={user.role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600 }}
                      />
                    </Stack>
                  </Box>
                  <Divider />
                  <MenuItem component={Link} to="/profile" onClick={() => setUserMenu(null)}>
                    <ListItemIcon>
                      <PersonRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('profile.title')}
                  </MenuItem>
                  <MenuItem component={Link} to="/2fa/setup" onClick={() => setUserMenu(null)}>
                    <ListItemIcon>
                      <SecurityRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nav.setup2fa')}
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/notifications/preferences"
                    onClick={() => setUserMenu(null)}
                  >
                    <ListItemIcon>
                      <NotificationsRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nav.notificationPrefs')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutRoundedIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography color="error">{t('nav.logout')}</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button component={Link} to="/login" variant="contained" size="medium">
                {t('nav.login')}
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {!isDesktop && user && (
        <MobileNavDrawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          items={visibleNav}
          pathname={location.pathname}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2.5, md: 3.5 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
